// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

// Tolerant parser for the latest-snapshot `payload_json` string.
// Two shapes are observed in the wild:
//   - Flat: `payload_json` is the verbatim `CycleSnapshotDto` (what the
//     Frontend types + fixtures assume — see `event-journal.d.ts`).
//   - Enveloped: `payload_json` is the full Pydantic
//     `EvolutionCycleSnapshotEvent` payload (envelope minus
//     event_id/event_name/metadata), with the DTO nested under `.snapshot`.
//     This is what `ebiose-core`'s `to_wire` actually emits and what the
//     Backend stores verbatim — surfaced by the first end-to-end seed run.
// Discriminator: `per_agent_rollup` at root (flat) vs under `.snapshot` (enveloped).
// Returns null on parse error or when neither shape is recognisable.
// The contract-side fix (Backend unwraps `payload -> 'snapshot'` on read,
// or wire emits the inner DTO directly for snapshot events) is tracked
// separately. This helper keeps both wire shapes consumable so the
// dashboard renders against real cycles without forcing a cross-stack change.
export const parseCycleSnapshotPayload = <T>(
  payloadJson: string,
): T | null => {
  let parsed: unknown
  try {
    parsed = JSON.parse(payloadJson)
  } catch {
    return null
  }
  if (parsed === null || typeof parsed !== "object") return null
  const obj = parsed as Record<string, unknown>
  if ("per_agent_rollup" in obj) return obj as T
  if ("snapshot" in obj && obj.snapshot !== null && typeof obj.snapshot === "object") {
    return obj.snapshot as T
  }
  return null
}
